/**
 * LitScholarPage.tsx — Enhanced Deep Dive with LitScholar
 *
 * ChatGPT-style interface for AI-powered literature analysis.
 * Features:
 * - ChatGPT-style left sidebar with chat history
 * - Article card header when articles attached
 * - Library search when no articles selected
 * - PubMed full-text search with PDF upload
 * - Multi-article comparison
 * - Consistent styling with LitDigests/LitHub
 *
 * PHI-Zero: no patient information allowed.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { CrossAppLinks } from '../components/CrossAppLinks';
import { litScholarAPI, libraryAPI, ragAPI, preferencesAPI } from '../api';
import type { RAGUploadResponse, RAGQueryResponse } from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import { SharedDigestFilterBar, type DigestProfileStat } from '../components/shared/SharedDigestFilterBar';
import { itemMatchesDigestProfile } from '../utils/digestArticleMatch';
import {
  getPersistedDigestProfileId,
  setPersistedDigestProfileId,
  pickDefaultDigestProfileId,
} from '../utils/sharedDigestSelection';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Send,
  Loader2,
  Brain,
  FileText,
  MessageSquare,
  GitCompareArrows,
  BookOpen,
  Sparkles,
  User,
  Bot,
  Plus,
  Trash2,
  Search,
  Upload,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Clock,
  AlertTriangle,
  X,
  Check,
  File,
  Download,
  Link,
  History,
} from 'lucide-react';
import type { Article } from '../types';

// ============================================================================
// TYPES
// ============================================================================
interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  type?: string;
  metadata?: {
    citations?: any[];
    confidence?: string;
    cached?: boolean;
  };
}

interface ChatSession {
  id: string;
  title: string;
  messages: Message[];
  articlePmids: string[];
  /** Digest profile this chat belongs to (LitScholar is single-digest). */
  profileId?: string | null;
  createdAt: Date;
  updatedAt: Date;
  hasFullText?: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================
const CARD_BG = [
  'bg-white',
  'bg-slate-50/70',
  'bg-gray-50/60',
  'bg-zinc-50/50',
];

// Helper to get pmid from article (handles optional pmid)
const getArticlePmid = (article: Article): string => {
  return article.pmid || article.article_id || '';
};

// ============================================================================
// ARTICLE CONTEXT CARD COMPONENT
// ============================================================================
interface ArticleContextCardProps {
  article: Article;
  index: number;
  onRemove?: (pmid: string) => void;
  hasFullText?: boolean;
  navV2On?: boolean;
  profileId?: string | null;
}

const ArticleContextCard: React.FC<ArticleContextCardProps> = ({
  article,
  index,
  onRemove,
  hasFullText,
  navV2On = false,
  profileId = null,
}) => {
  const [expanded, setExpanded] = useState(false);
  const shade = CARD_BG[index % CARD_BG.length];
  const pmid = article.pmid || article.article_id || 'unknown';

  return (
    <div
      className={`rounded-lg border border-slate-200 ${shade} transition-all`}
      data-testid={`context-article-${pmid}`}
    >
      <div className="px-4 py-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h4 className="responsive-title text-sm font-medium text-slate-900 line-clamp-2">
              {article.title}
            </h4>
            <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
              <span>{article.journal}</span>
              {article.pub_date && (
                <>
                  <span>·</span>
                  <span>{article.pub_date}</span>
                </>
              )}
              <span>·</span>
              <span>PMID: {pmid}</span>
              {hasFullText && (
                <Badge variant="secondary" className="h-4 px-1 text-[9px] bg-emerald-100 text-emerald-700">
                  Full Text
                </Badge>
              )}
            </div>
          </div>
          {onRemove && pmid && (
            <button
              onClick={() => onRemove(pmid)}
              className="text-slate-400 hover:text-red-500 p-1"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {article.abstract && !expanded && (
          <p className="mt-2 text-xs sm:text-sm text-slate-600 leading-relaxed line-clamp-2">
            {article.abstract}
          </p>
        )}
        {article.abstract && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 mt-2 text-[11px] text-sky-600 hover:text-sky-700"
          >
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {expanded ? 'Hide abstract' : 'Show abstract'}
          </button>
        )}
        {expanded && article.abstract && (
          <p className="mt-2 text-xs sm:text-sm text-slate-600 leading-relaxed bg-sky-50/50 p-2 sm:p-3 rounded">
            {article.abstract}
          </p>
        )}

        {/* Cross-App Links — Nav V2 */}
        {navV2On && pmid && pmid !== 'unknown' && (
          <CrossAppLinks pmid={pmid} profileId={profileId} originApp="litscholar" className="mt-2 px-0 border-0" />
        )}
      </div>
    </div>
  );
};

// ============================================================================
// LIBRARY SEARCH COMPONENT
// ============================================================================
interface LibrarySearchProps {
  articles: Article[];
  selectedArticles: Article[];
  onToggleArticle: (article: Article) => void;
  onClose: () => void;
}

const LibrarySearch: React.FC<LibrarySearchProps> = ({
  articles,
  selectedArticles,
  onToggleArticle,
  onClose,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredArticles = articles.filter(
    (a) =>
      !searchQuery.trim() ||
      a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (a.pmid && a.pmid.includes(searchQuery)) ||
      a.journal?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-shrink-0 sticky top-0 z-10 bg-background pb-3 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search your library..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="library-search-input"
          />
        </div>

        {selectedArticles.length > 0 && (
          <div className="bg-sky-50 border border-sky-200 rounded-lg p-3">
            <p className="text-xs font-medium text-sky-700 mb-2">
              {selectedArticles.length} article{selectedArticles.length !== 1 ? 's' : ''} selected
            </p>
            <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
              {selectedArticles.map((a) => (
                <Badge
                  key={a.pmid}
                  variant="secondary"
                  className="text-xs bg-white cursor-pointer hover:bg-red-50 max-md:h-auto max-md:whitespace-normal max-md:items-start max-md:py-1"
                  onClick={() => onToggleArticle(a)}
                >
                  <span className="responsive-title">{a.title}</span>
                  <X className="h-3 w-3 ml-1" />
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>

      <ScrollArea className="flex-1 min-h-0 my-4">
        <div className="space-y-2 pr-2">
          {filteredArticles.map((article) => {
            const isSelected = selectedArticles.some((a) => a.pmid === article.pmid);
            const articlePmid = article.pmid || article.article_id || '';
            return (
              <div
                key={articlePmid}
                onClick={() => onToggleArticle(article)}
                className={`p-3 rounded-lg border cursor-pointer transition-all ${
                  isSelected
                    ? 'border-sky-400 bg-sky-50'
                    : 'border-slate-200 hover:border-sky-200 hover:bg-slate-50'
                }`}
                data-testid={`library-article-${articlePmid}`}
              >
                <div className="flex items-start gap-2">
                  <div
                    className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 mt-0.5 ${
                      isSelected
                        ? 'bg-sky-500 border-sky-500'
                        : 'border-slate-300'
                    }`}
                  >
                    {isSelected && <Check className="h-3 w-3 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="responsive-title text-sm font-medium text-slate-900 line-clamp-2">
                      {article.title}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {article.journal} · PMID: {articlePmid}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
          {filteredArticles.length === 0 && (
            <div className="text-center py-8">
              <Search className="h-8 w-8 text-slate-200 mx-auto mb-2" />
              <p className="text-sm text-slate-500">
                {searchQuery ? 'No matching articles' : 'No articles in your library'}
              </p>
            </div>
          )}
        </div>
      </ScrollArea>

      <div className="flex-shrink-0 pt-2 border-t">
        <Button 
          onClick={onClose} 
          className="w-full bg-sky-600 hover:bg-sky-700"
          data-testid="library-search-done-btn"
        >
          Done ({selectedArticles.length} selected)
        </Button>
      </div>
    </div>
  );
};

// ============================================================================
// PUBMED FULL TEXT SEARCH COMPONENT WITH RAG
// ============================================================================
interface RAGDocumentState {
  document_id: string;
  filename: string;
  total_pages: number;
  total_chunks: number;
  pmid?: string;
}

interface FullTextSearchProps {
  onPdfUploaded: (docState: RAGDocumentState) => void;
  onPdfRemoved: () => void;
  selectedPmid?: string;
  ragDocument?: RAGDocumentState | null;
}

const FullTextSearch: React.FC<FullTextSearchProps> = ({
  onPdfUploaded,
  onPdfRemoved,
  selectedPmid,
  ragDocument,
}) => {
  const [searchQuery, setSearchQuery] = useState(selectedPmid || '');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const searchPubMed = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setError(null);
    setSearchResults([]);

    try {
      // Search PubMed for full text availability
      const response = await fetch(
        `https://eutils.ncbi.nlm.nih.gov/entrez/eutils/esearch.fcgi?db=pmc&term=${encodeURIComponent(
          searchQuery
        )}[pmid]&retmode=json`
      );
      const data = await response.json();

      if (data.esearchresult?.idlist?.length > 0) {
        // Article found in PMC (full text available)
        const pmcId = data.esearchresult.idlist[0];
        setSearchResults([
          {
            pmid: searchQuery,
            pmcId,
            title: `Article PMID ${searchQuery}`,
            hasFullText: true,
            pmcUrl: `https://www.ncbi.nlm.nih.gov/pmc/articles/PMC${pmcId}/`,
          },
        ]);
      } else {
        // Not in PMC, check if PMID exists
        const checkResponse = await fetch(
          `https://eutils.ncbi.nlm.nih.gov/entrez/eutils/esummary.fcgi?db=pubmed&id=${searchQuery}&retmode=json`
        );
        const checkData = await checkResponse.json();

        if (checkData.result?.[searchQuery]) {
          setSearchResults([
            {
              pmid: searchQuery,
              title: checkData.result[searchQuery].title || `Article PMID ${searchQuery}`,
              hasFullText: false,
            },
          ]);
          setError('Full text not available in PubMed Central. You can upload your own PDF.');
        } else {
          setError('Article not found. Check the PMID and try again.');
        }
      }
    } catch (err) {
      setError('Failed to search PubMed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      toast.error('PDF file too large. Maximum size is 20MB.');
      return;
    }

    setUploading(true);
    setUploadProgress(10);
    setError(null);

    try {
      // Simulate progress for UX
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 80));
      }, 500);

      // Upload and process PDF
      const response = await ragAPI.uploadPdf(file, searchQuery || undefined);

      clearInterval(progressInterval);
      setUploadProgress(100);

      // Notify parent
      onPdfUploaded({
        document_id: response.document_id,
        filename: response.filename,
        total_pages: response.total_pages,
        total_chunks: response.total_chunks,
        pmid: searchQuery || undefined,
      });

      toast.success(response.message);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Failed to process PDF';
      toast.error(detail);
      setError(detail);
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  const handleRemoveDocument = async () => {
    if (ragDocument) {
      try {
        await ragAPI.deleteDocument(ragDocument.document_id);
        onPdfRemoved();
        toast.success('Document removed');
      } catch {
        onPdfRemoved();
      }
    }
  };

  return (
    <div className="space-y-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-sky-600" />
          <h3 className="text-sm font-semibold text-slate-900">Full Text Analysis (RAG)</h3>
        </div>
        {ragDocument && (
          <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
            Full Text Ready
          </Badge>
        )}
      </div>

      {/* Active document display */}
      {ragDocument ? (
        <div className="bg-white rounded-lg border border-emerald-200 p-3">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-slate-900">{ragDocument.filename}</p>
              <p className="text-xs text-slate-500 mt-1">
                {ragDocument.total_pages} pages · {ragDocument.total_chunks} text segments indexed
              </p>
              <p className="text-xs text-emerald-600 mt-1">
                Ready for RAG-powered Q&A
              </p>
            </div>
            <button
              onClick={handleRemoveDocument}
              className="text-slate-400 hover:text-red-500 p-1"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-xs text-slate-500">
            Upload a PDF to enable deep full-text analysis. Your questions will be answered using
            AI retrieval from the actual article content.
          </p>

          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Enter PMID to search PMC..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchPubMed()}
                className="pl-10"
                data-testid="fulltext-search-input"
                disabled={uploading}
              />
            </div>
            <Button
              onClick={searchPubMed}
              disabled={searching || !searchQuery.trim() || uploading}
              variant="outline"
              className="shrink-0"
            >
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
            </Button>
          </div>

          {error && (
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-700">
              {error}
            </div>
          )}

          {searchResults.map((result) => (
            <div key={result.pmid} className="bg-white rounded-lg border p-3">
              <p className="responsive-title text-sm font-medium text-slate-900">{result.title}</p>
              <p className="text-xs text-slate-500 mt-1">PMID: {result.pmid}</p>
              {result.hasFullText ? (
                <a
                  href={result.pmcUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-2 text-xs text-sky-600 hover:text-sky-700"
                >
                  <ExternalLink className="h-3 w-3" />
                  Download from PubMed Central, then upload below
                </a>
              ) : (
                <p className="text-xs text-amber-600 mt-2">
                  Full text not freely available
                </p>
              )}
            </div>
          ))}

          <div className="border-t border-slate-200 pt-4">
            <p className="text-xs text-slate-500 mb-2">Upload a PDF you have access to:</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              onChange={handleFileUpload}
              className="hidden"
              disabled={uploading}
            />
            
            {uploading ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-sky-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Processing PDF...
                </div>
                <Progress value={uploadProgress} className="h-2" />
                <p className="text-xs text-slate-500">
                  Extracting text, creating embeddings for semantic search...
                </p>
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="w-full"
                data-testid="upload-pdf-btn"
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload PDF for Full-Text Analysis
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const LitScholarPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const navV2Enabled = useFeatureFlag('enable_navigation_v2');
  const routeParams = useParams<{ profileId?: string; pmid?: string }>();
  const isDigestRoute = navV2Enabled && location.pathname.includes('/litscholar/digests/');
  const isArticleRoute = navV2Enabled && location.pathname.includes('/litscholar/articles/');

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    if (sp.get('tab') !== 'portal') return;
    sp.delete('tab');
    const qs = sp.toString();
    navigate({ pathname: '/litportal', search: qs ? `?${qs}` : '' }, { replace: true });
  }, [location.search, navigate]);

  // State
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedArticles, setSelectedArticles] = useState<Article[]>([]);
  const [libraryArticles, setLibraryArticles] = useState<Article[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(true);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [quotaError, setQuotaError] = useState<{ message: string; retryAfter: number } | null>(null);
  const [showLibrarySearch, setShowLibrarySearch] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showFullTextSearch, setShowFullTextSearch] = useState(false);
  const [ragDocument, setRagDocument] = useState<RAGDocumentState | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // Digest filter state — LitScholar is always single-digest (no "All Digests")
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(
    () => getPersistedDigestProfileId()
  );
  const [profileStats, setProfileStats] = useState<DigestProfileStat[]>([]);
  const [profileStatsLoading, setProfileStatsLoading] = useState(true);

  const loadProfileStats = useCallback(async () => {
    try {
      setProfileStatsLoading(true);
      const response = await preferencesAPI.getProfilesScreenStats();
      const profiles = response.profiles || [];
      setProfileStats(profiles);
      // Ensure a digest is always selected
      setSelectedProfileId((prev) => {
        if (prev && profiles.some((p: DigestProfileStat) => p.profile_id === prev)) {
          return prev;
        }
        const persisted = getPersistedDigestProfileId();
        if (persisted && profiles.some((p: DigestProfileStat) => p.profile_id === persisted)) {
          return persisted;
        }
        const fallback = pickDefaultDigestProfileId(profiles);
        if (fallback) setPersistedDigestProfileId(fallback);
        return fallback;
      });
    } catch (error) {
      console.error('Failed to load profile stats:', error);
    } finally {
      setProfileStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadProfileStats();
  }, [loadProfileStats]);

  // ── Nav V2: Route-param sync for profileId (flag-gated) ──
  useEffect(() => {
    if (!navV2Enabled) return;
    if (isDigestRoute && routeParams.profileId) {
      setSelectedProfileId(routeParams.profileId);
      setPersistedDigestProfileId(routeParams.profileId);
    } else if (!isDigestRoute && !isArticleRoute && selectedProfileId) {
      // Root /litscholar — keep single-digest mode by redirecting to persisted digest
      navigate(`/litscholar/digests/${selectedProfileId}`, { replace: true });
    }
  }, [navV2Enabled, isDigestRoute, isArticleRoute, routeParams.profileId, location.pathname]);

  // Nav V2: Route-aware chip handler — switching digest switches chat
  const handleProfileSelect = useCallback((id: string | null) => {
    if (!id) return; // LitScholar never allows "All Digests"
    if (id === selectedProfileId) return;
    setPersistedDigestProfileId(id);
    // Clear current chat when switching digest; load digest-scoped history below
    setCurrentSessionId(null);
    setMessages([]);
    setSelectedArticles([]);
    setRagDocument(null);
    if (navV2Enabled) {
      navigate(`/litscholar/digests/${id}`);
    } else {
      setSelectedProfileId(id);
    }
  }, [navV2Enabled, navigate, selectedProfileId]);

  // Track which path-pmid has been preselected to enforce idempotency
  const [preselectedPathPmid, setPreselectedPathPmid] = useState<string | null>(null);

  // Determine entry mode
  const fromLibrary = location.state?.articles || location.state?.pmids || new URLSearchParams(location.search).get('pmid');

  // Load library articles
  useEffect(() => {
    const loadLibrary = async () => {
      setLibraryLoading(true);
      try {
        const response = await libraryAPI.getLibrary({ limit: 100 });
        // Sort articles newest-to-oldest by saved_at
        const articles = (response.articles || []).sort((a: any, b: any) => {
          const dateA = a.saved_at ? new Date(a.saved_at).getTime() : 0;
          const dateB = b.saved_at ? new Date(b.saved_at).getTime() : 0;
          return dateB - dateA;
        });
        setLibraryArticles(articles);
      } catch (error) {
        console.error('Failed to load library:', error);
      } finally {
        setLibraryLoading(false);
      }
    };
    loadLibrary();
  }, []);
  
  // Filter library articles by selected digest profile (required — no cross-digest search)
  const filteredLibraryArticles = React.useMemo(() => {
    if (!selectedProfileId) return [];
    const profile = profileStats.find((p) => p.profile_id === selectedProfileId);
    if (profile) {
      return libraryArticles.filter((a: any) => itemMatchesDigestProfile(a, profile));
    }
    return libraryArticles.filter(
      (a: any) =>
        a.profile_id === selectedProfileId ||
        a.digest_profile_id === selectedProfileId ||
        a.specialty_id === selectedProfileId
    );
  }, [libraryArticles, selectedProfileId, profileStats]);

  // Sessions belonging to the active digest only
  const digestSessions = React.useMemo(() => {
    if (!selectedProfileId) return [];
    const profile = profileStats.find((p) => p.profile_id === selectedProfileId);
    return sessions.filter((s) => {
      if (s.profileId) return s.profileId === selectedProfileId;
      // Legacy sessions without profileId — keep if articles belong to this digest
      if (!s.articlePmids?.length || !profile) return false;
      return s.articlePmids.every((pmid) => {
        const art = libraryArticles.find((a) => getArticlePmid(a) === pmid);
        return art ? itemMatchesDigestProfile(art as any, profile) : false;
      });
    });
  }, [sessions, selectedProfileId, profileStats, libraryArticles]);

  // Handle articles from navigation
  // Precedence: location.state.articles > path pmid > location.state.pmids > query pmid
  useEffect(() => {
    const state = location.state as { articles?: Article[]; pmids?: string[] } | null;
    const searchParams = new URLSearchParams(location.search);

    // 1. Direct article objects from navigation (most reliable — no library lookup needed)
    if (state?.articles && state.articles.length > 0) {
      setSelectedArticles(state.articles);
      return;
    }

    if (libraryArticles.length === 0) return;

    const resolveFromLibrary = (pmid: string): Article | undefined =>
      libraryArticles.find((a) => getArticlePmid(a) === pmid);

    // 2. Path pmid (Nav V2)
    const pathPmid = navV2Enabled && isArticleRoute ? routeParams.pmid : null;
    if (pathPmid) {
      if (preselectedPathPmid === pathPmid) return;
      const found = resolveFromLibrary(pathPmid);
      if (found) {
        setSelectedArticles([found]);
        setPreselectedPathPmid(pathPmid);
      }
      return;
    }

    // 3. location.state.pmids
    if (state?.pmids && state.pmids.length > 0) {
      const resolved = state.pmids
        .map((pmid: string) => resolveFromLibrary(pmid))
        .filter(Boolean) as Article[];
      if (resolved.length > 0) {
        setSelectedArticles(resolved);
      }
      return;
    }

    // 4. Query param pmid
    const urlPmid = searchParams.get('pmid');
    if (urlPmid) {
      const found = resolveFromLibrary(urlPmid);
      if (found) {
        setSelectedArticles([found]);
      }
    }
  }, [location.state, location.search, location.pathname, libraryArticles, libraryLoading, navV2Enabled, isArticleRoute, routeParams.pmid, preselectedPathPmid]);

  // Load/save sessions from localStorage
  useEffect(() => {
    const savedSessions = localStorage.getItem('litscholar_sessions_v2');
    if (savedSessions) {
      try {
        const parsed = JSON.parse(savedSessions);
        setSessions(
          parsed.map((s: any) => ({
            ...s,
            createdAt: new Date(s.createdAt),
            updatedAt: new Date(s.updatedAt),
            messages: s.messages.map((m: any) => ({ ...m, timestamp: new Date(m.timestamp) })),
          }))
        );
      } catch (e) {
        console.error('Failed to parse sessions:', e);
      }
    }
  }, []);

  useEffect(() => {
    if (sessions.length > 0) {
      localStorage.setItem('litscholar_sessions_v2', JSON.stringify(sessions));
    }
  }, [sessions]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Session management
  const createNewSession = () => {
    const newSession: ChatSession = {
      id: crypto.randomUUID(),
      title: 'New Chat',
      messages: [],
      articlePmids: selectedArticles.map((a) => getArticlePmid(a)).filter(Boolean),
      profileId: selectedProfileId,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    setSessions((prev) => [newSession, ...prev]);
    setCurrentSessionId(newSession.id);
    setMessages([]);
    setSelectedArticles([]);
  };

  const loadSession = (sessionId: string) => {
    const session = sessions.find((s) => s.id === sessionId);
    if (session) {
      setCurrentSessionId(sessionId);
      setMessages(session.messages);
      const articles = session.articlePmids
        .map((pmid) => libraryArticles.find((a) => getArticlePmid(a) === pmid))
        .filter(Boolean) as Article[];
      setSelectedArticles(articles);
      setShowHistory(false);
    }
  };

  const getSessionDisplayTitle = (session: ChatSession): string => {
    if (session.articlePmids?.length === 1) {
      const art = libraryArticles.find((a) => getArticlePmid(a) === session.articlePmids[0]);
      if (art?.title) return art.title;
    }
    if (session.articlePmids?.length > 1) {
      return `Article Deep Dive (${session.articlePmids.length} articles)`;
    }
    return session.title;
  };

  const deleteSession = (sessionId: string) => {
    setSessions((prev) => prev.filter((s) => s.id !== sessionId));
    if (currentSessionId === sessionId) {
      setCurrentSessionId(null);
      setMessages([]);
      setSelectedArticles([]);
    }
  };

  // Resolve a display title from selected articles (deterministic, no AI)
  const resolveSessionTitle = (pmids: string[], fallback: string): string => {
    if (pmids.length === 1) {
      const art = selectedArticles.find(a => getArticlePmid(a) === pmids[0])
        || libraryArticles.find(a => getArticlePmid(a) === pmids[0]);
      if (art?.title) return art.title;
    }
    if (pmids.length > 1) return `Article Deep Dive (${pmids.length} articles)`;
    return fallback;
  };

  const updateCurrentSession = (newMessages: Message[]) => {
    if (!currentSessionId) {
      const questionFallback =
        newMessages.find((m) => m.role === 'user')?.content.slice(0, 50) || 'New Chat';
      const pmids = selectedArticles.map((a) => getArticlePmid(a)).filter(Boolean);
      const title = resolveSessionTitle(pmids, questionFallback);
      const newSession: ChatSession = {
        id: crypto.randomUUID(),
        title,
        messages: newMessages,
        articlePmids: selectedArticles.map((a) => getArticlePmid(a)).filter(Boolean),
        profileId: selectedProfileId,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      setSessions((prev) => [newSession, ...prev]);
      setCurrentSessionId(newSession.id);
    } else {
      setSessions((prev) =>
        prev.map((s) =>
          s.id === currentSessionId ? { ...s, messages: newMessages, updatedAt: new Date() } : s
        )
      );
    }
  };

  // Send message
  const sendMessage = async (content: string, type = 'general') => {
    if (!content.trim() || isLoading) return;
    if (quotaError) {
      toast.error('Daily quota exceeded. Please try again later.');
      return;
    }

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
      type,
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInputValue('');
    setIsLoading(true);

    try {
      let response;
      const pmid = getArticlePmid(selectedArticles[0] || {} as Article);
      const pmids = selectedArticles.map((a) => getArticlePmid(a)).filter(Boolean);

      if (type === 'evidence_brief' && pmid) {
        response = await litScholarAPI.evidenceBrief(pmid);
        const assistantMessage: Message = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: formatEvidenceBrief(response),
          timestamp: new Date(),
          type: 'evidence_brief',
          metadata: { citations: response.citations, cached: response.cached },
        };
        const updatedMessages = [...newMessages, assistantMessage];
        setMessages(updatedMessages);
        updateCurrentSession(updatedMessages);
      } else if (type === 'compare_studies' && pmids.length >= 2) {
        response = await litScholarAPI.compareStudies(pmids);
        const assistantMessage: Message = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: formatCompareStudies(response),
          timestamp: new Date(),
          type: 'compare_studies',
          metadata: { citations: response.citations, cached: response.cached },
        };
        const updatedMessages = [...newMessages, assistantMessage];
        setMessages(updatedMessages);
        updateCurrentSession(updatedMessages);
      } else if (pmid) {
        // If RAG document is available, use RAG query for better answers
        if (ragDocument) {
          const ragResponse = await ragAPI.query({
            document_id: ragDocument.document_id,
            query: content,
            article_title: selectedArticles[0]?.title,
            article_abstract: selectedArticles[0]?.abstract,
            k: 5,
          });
          
          // Format sources for display
          const sourcesText = ragResponse.sources.length > 0
            ? `\n\n**Sources:** ${ragResponse.sources.map(s => `Page ${s.page}`).join(', ')}`
            : '';
          
          const assistantMessage: Message = {
            id: crypto.randomUUID(),
            role: 'assistant',
            content: ragResponse.answer + sourcesText,
            timestamp: new Date(),
            type: 'rag_query',
            metadata: {
              citations: ragResponse.sources,
            },
          };
          const updatedMessages = [...newMessages, assistantMessage];
          setMessages(updatedMessages);
          updateCurrentSession(updatedMessages);
        } else {
          // Fallback to abstract-based analysis — API accepts pmid as string[]
          response = await litScholarAPI.askArticle(pmids.length > 0 ? pmids : [pmid], content);
          const assistantMessage: Message = {
            id: crypto.randomUUID(),
            role: 'assistant',
            content: response.answer || "I couldn't find a relevant answer in the article.",
            timestamp: new Date(),
            type: 'ask_article',
            metadata: {
              citations: response.citations,
              confidence: response.confidence,
              cached: response.cached,
            },
          };
          const updatedMessages = [...newMessages, assistantMessage];
          setMessages(updatedMessages);
          updateCurrentSession(updatedMessages);
        }
      } else {
        const assistantMessage: Message = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content:
            'Please select at least one article to analyze. Use the "Select Articles" button to choose from your library.',
          timestamp: new Date(),
        };
        const updatedMessages = [...newMessages, assistantMessage];
        setMessages(updatedMessages);
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      if (detail?.error_code === 'copilot_quota_exceeded') {
        setQuotaError({
          message: detail.message || 'Daily limit reached.',
          retryAfter: detail.retry_after_seconds || 3600,
        });
        toast.error('LitScholar limit reached for today.');
      } else if (detail?.error_code === 'phi_detected') {
        toast.error(detail.message || 'PHI detected. Please rephrase.');
      } else {
        toast.error('Failed to get response. Please try again.');
      }
      setMessages(messages);
    } finally {
      setIsLoading(false);
    }
  };

  const formatEvidenceBrief = (response: any) => {
    const brief = response.evidence_brief;
    if (!brief) return 'Unable to generate evidence brief.';

    let content = '';
    if (brief.clinical_bottom_line) {
      content += `**Clinical Bottom Line**\n${brief.clinical_bottom_line}\n\n`;
    }
    if (brief.summary) {
      content += `**Summary**\n${brief.summary}\n\n`;
    }
    if (brief.key_findings?.length) {
      content += `**Key Findings**\n${brief.key_findings.map((f: string) => `• ${f}`).join('\n')}\n\n`;
    }
    if (brief.limitations?.length) {
      content += `**Limitations**\n${brief.limitations.map((l: string) => `• ${l}`).join('\n')}\n\n`;
    }
    if (response.disclaimer) {
      content += `\n_${response.disclaimer}_`;
    }
    return content;
  };

  const formatCompareStudies = (response: any) => {
    let content = '';
    if (response.comparison_title) {
      content += `**${response.comparison_title}**\n\n`;
    }
    if (response.table) {
      content += '**Comparison Table**\n';
      if (response.table.columns) {
        content += `| ${response.table.columns.join(' | ')} |\n`;
        content += `| ${response.table.columns.map(() => '---').join(' | ')} |\n`;
      }
      if (response.table.rows) {
        response.table.rows.forEach((row: string[]) => {
          content += `| ${row.join(' | ')} |\n`;
        });
      }
      content += '\n';
    }
    if (response.synthesis) {
      content += `**Synthesis**\n${response.synthesis}\n\n`;
    }
    if (response.disclaimer) {
      content += `\n_${response.disclaimer}_`;
    }
    return content;
  };

  const toggleArticleSelection = (article: Article) => {
    setSelectedArticles((prev) => {
      const articlePmid = getArticlePmid(article);
      const isSelected = prev.some((a) => getArticlePmid(a) === articlePmid);
      if (isSelected) {
        return prev.filter((a) => getArticlePmid(a) !== articlePmid);
      } else if (prev.length < 5) {
        return [...prev, article];
      } else {
        toast.error('Maximum 5 articles can be selected');
        return prev;
      }
    });
  };

  const renderMessage = (message: Message) => {
    const isUser = message.role === 'user';
    const isSystem = message.role === 'system';

    if (isSystem) {
      return (
        <div key={message.id} className="flex justify-center my-4">
          <div className="bg-slate-100 text-slate-600 text-xs px-4 py-2 rounded-full">
            {message.content}
          </div>
        </div>
      );
    }

    return (
      <div
        key={message.id}
        className={`flex gap-3 mb-4 ${isUser ? 'justify-end' : 'justify-start'}`}
        data-testid={`message-${message.id}`}
      >
        {!isUser && (
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shrink-0">
            <Bot className="h-4 w-4 text-white" />
          </div>
        )}
        <div
          className={`max-w-[80%] rounded-2xl px-4 py-3 ${
            isUser ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-900'
          }`}
        >
          <div className="text-sm whitespace-pre-wrap leading-relaxed">
            {message.content.split('\n').map((line, i) => {
              if (line.startsWith('**') && line.endsWith('**')) {
                return (
                  <p key={i} className="font-semibold mt-2 mb-1">
                    {line.slice(2, -2)}
                  </p>
                );
              }
              if (line.startsWith('• ')) {
                return (
                  <p key={i} className="ml-2">
                    {line}
                  </p>
                );
              }
              if (line.startsWith('_') && line.endsWith('_')) {
                return (
                  <p key={i} className="text-xs opacity-70 mt-2 italic">
                    {line.slice(1, -1)}
                  </p>
                );
              }
              return <p key={i}>{line}</p>;
            })}
          </div>
          {message.metadata?.confidence && (
            <Badge variant="secondary" className="mt-2 text-[10px]">
              Confidence: {message.metadata.confidence}
            </Badge>
          )}
          {message.metadata?.cached && (
            <Badge variant="outline" className="mt-2 ml-1 text-[10px]">
              cached
            </Badge>
          )}
        </div>
        {isUser && (
          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center shrink-0">
            <User className="h-4 w-4 text-slate-600" />
          </div>
        )}
      </div>
    );
  };

  const hasConversation = messages.some((m) => m.role === 'user' || m.role === 'assistant');

  const quickPrompts = [
    {
      id: 'evidence_brief',
      label: 'Generate Evidence Brief',
      icon: FileText,
      description: 'Structured summary of the article',
      disabled: selectedArticles.length === 0,
      action: () => sendMessage('Generate an evidence brief for this article', 'evidence_brief'),
    },
    {
      id: 'key_findings',
      label: 'What are the key findings?',
      icon: Sparkles,
      description: 'Extract main conclusions',
      disabled: selectedArticles.length === 0,
      action: () => sendMessage('What are the key findings of this study?'),
    },
    {
      id: 'methodology',
      label: 'Explain the methodology',
      icon: Brain,
      description: 'Understand study design',
      disabled: selectedArticles.length === 0,
      action: () => sendMessage('Can you explain the methodology used in this study?'),
    },
    {
      id: 'compare',
      label: 'Compare Studies',
      icon: GitCompareArrows,
      description: 'Compare 2-5 articles',
      disabled: selectedArticles.length < 2 || selectedArticles.length > 5,
      action: () => sendMessage('Compare these studies side by side', 'compare_studies'),
    },
  ];

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <div
      className="flex flex-col md:overflow-hidden"
      data-testid="litscholar-page"
    >
      {/* Digest Filter Bar */}
      <SharedDigestFilterBar
        selectedProfileId={selectedProfileId}
        onSelectProfile={handleProfileSelect}
        profiles={profileStats}
        statsLoading={profileStatsLoading}
        onRefreshStats={loadProfileStats}
        showUnscreenedBadge={false}
        showSavedPercent={true}
        showPrimaryStar={true}
        hideAllOption
      />
      
      {/* Header — sticky */}
      <div className="shrink-0 sticky top-0 z-20 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="-ml-2 shrink-0">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <div className="flex items-center gap-1.5 shrink-0 flex-nowrap overflow-x-auto">
              <Button variant="outline" size="sm" onClick={createNewSession} data-testid="new-chat-btn" className="shrink-0 whitespace-nowrap">
                <Plus className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">New Chat</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowLibrarySearch(true)}
                data-testid="select-articles-btn"
                className="shrink-0 whitespace-nowrap"
              >
                <BookOpen className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">
                  {selectedArticles.length > 0 ? `${selectedArticles.length} Articles` : 'Select Articles'}
                </span>
                {selectedArticles.length > 0 && (
                  <span className="sm:hidden ml-1 tabular-nums">{selectedArticles.length}</span>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowHistory(true)}
                data-testid="history-btn"
                className="shrink-0 whitespace-nowrap"
              >
                <History className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">History</span>
                {digestSessions.length > 0 && (
                  <span className="ml-1 text-[10px] tabular-nums text-slate-500">
                    {Math.min(digestSessions.length, 99)}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content — explicit height on desktop so sidebar + chat fill the viewport */}
      <div className="flex flex-col md:flex-row md:min-h-0 md:overflow-hidden md:h-[calc(100dvh-var(--litpulse-chrome-height,10rem)-4.5rem)]">
        {/* Left Sidebar — full-height chat history on desktop */}
        <div
          className={`hidden md:flex flex-col shrink-0 min-h-0 border-r bg-white transition-all ${
            sidebarCollapsed ? 'w-12' : 'w-64'
          }`}
        >
          <div className="shrink-0 sticky top-0 z-10 bg-white p-3 border-b flex items-center justify-between">
            {!sidebarCollapsed && (
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Chat History
              </h3>
            )}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="text-slate-400 hover:text-slate-600 p-1"
            >
              {sidebarCollapsed ? <ChevronDown className="h-4 w-4 rotate-[-90deg]" /> : <ChevronDown className="h-4 w-4 rotate-90" />}
            </button>
          </div>
          {!sidebarCollapsed && (
            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className="p-3 space-y-2">
                {digestSessions.slice(0, 20).map((session) => (
                  <div
                    key={session.id}
                    className={`group p-2 rounded-lg cursor-pointer transition-colors ${
                      currentSessionId === session.id
                        ? 'bg-sky-100 border border-sky-200'
                        : 'hover:bg-slate-50'
                    }`}
                    onClick={() => loadSession(session.id)}
                  >
                    <p className="responsive-title text-sm font-medium text-slate-700 truncate">
                      {getSessionDisplayTitle(session)}
                    </p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[10px] text-slate-400 flex items-center gap-1">
                        <Clock className="h-2.5 w-2.5" />
                        {new Date(session.updatedAt).toLocaleDateString()}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteSession(session.id);
                        }}
                        className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                ))}
                {digestSessions.length === 0 && (
                  <p className="text-xs text-slate-400 text-center py-4">No chat history</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Main Chat Area */}
        <div className="flex flex-col min-w-0 md:flex-1 md:min-h-0 md:overflow-hidden">
          {/* Scrollable content — messages, articles, prompts */}
          <div className="p-4 order-1 md:order-2 md:flex-1 md:min-h-0 md:overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]">
            <div className="max-w-3xl mx-auto">
              {/* Nav V2: Article not found */}
              {navV2Enabled && isArticleRoute && routeParams.pmid && selectedArticles.length === 0 && !libraryLoading && (
                !libraryArticles.some((a) => getArticlePmid(a) === routeParams.pmid)
              ) && (
                <div className="border bg-slate-50/50 rounded-lg p-6 text-center mb-4" data-testid="litscholar-article-not-found">
                  <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <h3 className="font-medium text-slate-800 mb-1">This article isn't available for analysis.</h3>
                  <p className="text-sm text-slate-500 mb-3">PMID {routeParams.pmid} — Save it from LitScreen first.</p>
                  <button onClick={() => navigate('/litscholar')} className="text-sm text-sky-600 hover:text-sky-700 font-medium">
                    ← Back to LitScholar
                  </button>
                </div>
              )}

              {/* Article Context Cards */}
              {selectedArticles.length > 0 && (
                <div className="border bg-slate-50/50 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-sky-600" />
                      Analyzing {selectedArticles.length} Article{selectedArticles.length !== 1 ? 's' : ''}
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowFullTextSearch(!showFullTextSearch)}
                      className="text-xs"
                    >
                      <Search className="h-3.5 w-3.5 mr-1" />
                      Full Text Search
                    </Button>
                  </div>

                  {showFullTextSearch && (
                    <FullTextSearch
                      onPdfUploaded={(doc) => setRagDocument(doc)}
                      onPdfRemoved={() => setRagDocument(null)}
                      selectedPmid={getArticlePmid(selectedArticles[0] || {} as Article)}
                      ragDocument={ragDocument}
                    />
                  )}

                  <div className="space-y-2 mt-3">
                    {selectedArticles.map((article, index) => {
                      const articlePmid = getArticlePmid(article);
                      return (
                        <ArticleContextCard
                          key={articlePmid || index}
                          article={article}
                          index={index}
                          onRemove={(pmid) =>
                            setSelectedArticles((prev) => prev.filter((a) => getArticlePmid(a) !== pmid))
                          }
                          hasFullText={!!ragDocument && ragDocument.pmid === articlePmid}
                          navV2On={navV2Enabled}
                          profileId={selectedProfileId}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Quota Warning */}
              {quotaError && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-amber-800 font-medium text-sm">{quotaError.message}</p>
                      <p className="text-xs text-amber-600 mt-1">
                        Try again in ~{Math.ceil(quotaError.retryAfter / 3600)} hour(s).
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Library search — visible until conversation starts */}
              {!hasConversation && (
                <div className="relative mb-4 max-w-xl mx-auto">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                  <Input
                    placeholder={
                      selectedArticles.length > 0
                        ? 'Search library to add more articles...'
                        : 'Search your library...'
                    }
                    onFocus={() => setShowLibrarySearch(true)}
                    onClick={() => setShowLibrarySearch(true)}
                    readOnly
                    className="pl-10 bg-white cursor-pointer"
                    data-testid="library-search-trigger"
                  />
                </div>
              )}

              {/* Empty state (no articles selected) */}
              {messages.length === 0 && selectedArticles.length === 0 && (
                <div className="text-center py-10">
                  <p className="text-slate-500 text-sm max-w-md mx-auto mb-5">
                    Select articles from your library to start analyzing.
                  </p>
                  <Button
                    onClick={() => setShowLibrarySearch(true)}
                    variant="outline"
                    className="border-sky-200 text-sky-700 hover:bg-sky-50"
                    data-testid="select-articles-welcome-btn"
                  >
                    <BookOpen className="h-4 w-4 mr-2" />
                    Browse Library
                  </Button>
                  <p className="text-[10px] text-slate-400 mt-8">
                    No patient information. For literature discussion only.
                  </p>
                </div>
              )}

              {/* Quick Prompts (articles selected, no conversation yet) */}
              {!hasConversation && selectedArticles.length > 0 && (
                <div className="py-4">
                  <p className="text-xs text-slate-500 text-center mb-3">
                    Quick prompts to get started:
                  </p>
                  <div className="flex flex-wrap justify-center gap-2 max-w-xl mx-auto">
                    {quickPrompts.map((prompt) => {
                      const Icon = prompt.icon;
                      return (
                        <button
                          key={prompt.id}
                          onClick={prompt.action}
                          disabled={prompt.disabled || !!quotaError}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:bg-sky-50 hover:border-sky-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all text-xs"
                          data-testid={`quick-prompt-${prompt.id}`}
                        >
                          <Icon className="h-3.5 w-3.5 text-sky-600" />
                          <span className="text-slate-700">{prompt.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-slate-400 text-center mt-4 max-md:hidden">
                    Or type your own question in the box above
                  </p>
                  <p className="text-[10px] text-slate-400 text-center mt-4 md:hidden">
                    Or type your own question below
                  </p>
                </div>
              )}

              {/* Messages */}
              {messages.map(renderMessage)}

              {/* Loading */}
              {isLoading && (
                <div className="flex gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shrink-0">
                    <Bot className="h-4 w-4 text-white" />
                  </div>
                  <div className="bg-slate-100 rounded-2xl px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-sky-600" />
                      <span className="text-sm text-slate-600">Analyzing...</span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input Area — sticky at bottom on mobile, top on desktop */}
          <div className="shrink-0 z-30 order-2 md:order-1 sticky bottom-0 flex justify-center px-3 py-2 border-t border-slate-200/80 bg-white shadow-[0_-4px_24px_rgba(15,23,42,0.06)] pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:px-4 md:relative md:py-4 md:shadow-none md:border-t-0">
            <div
              className="flex w-full max-w-2xl items-end gap-2 rounded-3xl border border-slate-200 bg-white p-1.5 max-md:shadow-sm md:rounded-2xl md:border-slate-200/70 md:bg-white/90 md:backdrop-blur-md md:p-2 md:shadow-[0_8px_30px_rgba(15,23,42,0.08)] md:ring-1 md:ring-slate-900/5"
              data-testid="chat-input-container"
            >
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(inputValue);
                  }
                }}
                placeholder={
                  selectedArticles.length === 0
                    ? 'Select articles to start chatting...'
                    : 'Message LitScholar...'
                }
                disabled={isLoading || !!quotaError}
                rows={1}
                className="min-h-[44px] max-h-32 flex-1 resize-none border-0 bg-transparent px-3 py-2.5 text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-60 md:min-h-[52px]"
                data-testid="chat-input"
              />
              <Button
                onClick={() => sendMessage(inputValue)}
                disabled={!inputValue.trim() || isLoading || selectedArticles.length === 0 || !!quotaError}
                className="mb-0.5 h-9 w-9 shrink-0 rounded-full bg-sky-600 hover:bg-sky-700 shadow-sm md:h-11 md:w-11 md:rounded-xl"
                data-testid="send-btn"
              >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Chat History Sheet */}
      <Sheet open={showHistory} onOpenChange={setShowHistory}>
        <SheetContent side="left" className="w-full sm:max-w-sm flex flex-col">
          <SheetHeader className="flex-shrink-0">
            <SheetTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-sky-600" />
              Chat History
            </SheetTitle>
          </SheetHeader>
          <div className="mt-4 flex-1 min-h-0 overflow-y-auto">
            {digestSessions.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-10 px-4">
                No chat history yet. Start a conversation to see it here.
              </p>
            ) : (
              <div className="space-y-2 pr-1">
                {digestSessions.slice(0, 40).map((session) => (
                  <div
                    key={session.id}
                    className={`group p-3 rounded-xl border cursor-pointer transition-colors ${
                      currentSessionId === session.id
                        ? 'bg-sky-50 border-sky-200'
                        : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                    onClick={() => loadSession(session.id)}
                    data-testid={`history-session-${session.id}`}
                  >
                    <p className="text-sm font-medium text-slate-800 line-clamp-2 leading-snug">
                      {getSessionDisplayTitle(session)}
                    </p>
                    <div className="flex items-center justify-between mt-2 gap-2">
                      <span className="text-[11px] text-slate-400 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {new Date(session.updatedAt).toLocaleString()}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteSession(session.id);
                        }}
                        className="text-slate-400 hover:text-red-500 p-1 rounded-md hover:bg-red-50"
                        aria-label="Delete chat"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {session.articlePmids?.length > 0 && (
                      <p className="mt-1.5 text-[10px] text-slate-400">
                        {session.articlePmids.length} article
                        {session.articlePmids.length !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Library Search Sheet */}
      <Sheet open={showLibrarySearch} onOpenChange={setShowLibrarySearch}>
        <SheetContent side="right" className="w-full sm:max-w-md flex flex-col">
          <SheetHeader className="flex-shrink-0">
            <SheetTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-sky-600" />
              Select Articles
            </SheetTitle>
          </SheetHeader>
          <div className="mt-4 flex-1 min-h-0">
            {libraryLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-20 w-full" />
                ))}
              </div>
            ) : (
              <LibrarySearch
                articles={filteredLibraryArticles}
                selectedArticles={selectedArticles}
                onToggleArticle={toggleArticleSelection}
                onClose={() => setShowLibrarySearch(false)}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default LitScholarPage;
